import React, { useEffect, useMemo, useState } from 'react';
import styles from './GalleryLightbox.module.css';
import { useTranslation } from 'react-i18next';

const GalleryLightbox = ({ images = [], initialIndex = 0, isOpen, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const { t } = useTranslation('galleryLightbox');

  useEffect(() => {
    if (isOpen) {
      setIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const safeImages = useMemo(() => (Array.isArray(images) ? images : []), [images]);
  const total = safeImages.length;
  const current = total > 0 ? safeImages[Math.min(Math.max(index, 0), total - 1)] : null;

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (!total) return;
      if (e.key === 'ArrowRight') {
        setIndex((prev) => (prev + 1) % total);
      }
      if (e.key === 'ArrowLeft') {
        setIndex((prev) => (prev - 1 + total) % total);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, total]);

  if (!isOpen || !current) return null;

  const showNav = total > 1;

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label={t('ariaLabel')}>
      <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('close')}>×</button>

      {showNav && (
        <button
          type="button"
          className={`${styles.navBtn} ${styles.prevBtn}`}
          onClick={(e) => {
            e.stopPropagation();
            setIndex((prev) => (prev - 1 + total) % total);
          }}
          aria-label={t('prev')}
        >
          ←
        </button>
      )}

      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        <img src={current.url} alt={current.caption || 'gallery'} className={styles.image} />
        {current.caption && <p className={styles.caption}>{current.caption}</p>}
        <p className={styles.count}>{index + 1} / {total}</p>
      </div>

      {showNav && (
        <button
          type="button"
          className={`${styles.navBtn} ${styles.nextBtn}`}
          onClick={(e) => {
            e.stopPropagation();
            setIndex((prev) => (prev + 1) % total);
          }}
          aria-label={t('next')}
        >
          →
        </button>
      )}
    </div>
  );
};

export default GalleryLightbox;
